import Dexie, { type Table } from 'dexie';
import type { SyncQueueEntry } from './types';

class OfflineDatabase extends Dexie {
  syncQueue!: Table<SyncQueueEntry, number>;

  constructor(userId: number) {
    super(`vivatis-pdb-offline-${userId}`);
    this.version(1).stores({
      syncQueue: '++localId, status, createdAt',
    });
  }
}

let currentDb: OfflineDatabase | null = null;
let currentUserId: number | null = null;

export function openOfflineDb(userId: number): OfflineDatabase {
  if (currentDb && currentUserId === userId) {
    return currentDb;
  }
  if (currentDb) {
    currentDb.close();
  }
  currentDb = new OfflineDatabase(userId);
  currentUserId = userId;
  return currentDb;
}

export function closeOfflineDb(): void {
  if (currentDb) {
    currentDb.close();
    currentDb = null;
    currentUserId = null;
  }
}

export function getOfflineDb(): OfflineDatabase | null {
  return currentDb;
}

export async function enqueue(
  feature: string,
  type: string,
  payload: Record<string, unknown>,
  tempId: string | null = null,
): Promise<void> {
  const db = getOfflineDb();
  if (!db) return;
  await db.syncQueue.add({
    feature,
    type,
    payload,
    tempId,
    status: 'pending',
    createdAt: Date.now(),
    error: null,
  });
  window.dispatchEvent(new Event('sync-queue-changed'));
}

export async function getPendingCount(): Promise<number> {
  const db = getOfflineDb();
  if (!db) return 0;
  return db.syncQueue.where('status').equals('pending').count();
}

export async function clearSyncQueue(): Promise<void> {
  const db = getOfflineDb();
  if (!db) return;
  await db.syncQueue.clear();
}

let tempIdCounter = 0;

export function generateTempId(): string {
  return `temp_${Date.now()}_${++tempIdCounter}`;
}
